import * as React from "react";
import { DayPicker, type Matcher } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { isSameDay } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const now = new Date();
const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
const TZ = "America/Sao_Paulo";
const dayKey = (date: Date) => formatInTimeZone(date, TZ, "yyyy-MM-dd");

function Calendar({
  className,
  classNames,
  showOutsideDays = false,
  disabled: disabledProp,
  ...props
}: CalendarProps) {
  const [month, setMonth] = React.useState<Date>(firstDayOfMonth);

  const disabled = React.useMemo<Matcher>(() => {
    const todayKey = dayKey(new Date());

    return (date) => {
      const key = dayKey(date);

      if (key === todayKey) return false;
      if (key < todayKey) return true;

      if (typeof disabledProp === "function") {
        return Boolean(disabledProp(date));
      }

      if (Array.isArray(disabledProp)) {
        return disabledProp.some((matcher) =>
          matcher instanceof Date ? isSameDay(matcher, date) : false,
        );
      }

      return false;
    };
  }, [disabledProp]);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks
      defaultMonth={firstDayOfMonth}
      month={month}
      onMonthChange={setMonth}
      disabled={disabled}
      className={cn("ap-calendar", className)}
      classNames={{
        root: "ap-calendar-root",
        months: "ap-calendar-months",
        month: "ap-calendar-month",
        month_caption: "ap-calendar-caption",
        caption_label: "ap-calendar-caption-label",
        nav: "ap-calendar-nav",
        button_previous: "ap-calendar-nav-button ap-calendar-nav-previous",
        button_next: "ap-calendar-nav-button ap-calendar-nav-next",
        month_grid: "ap-calendar-grid",
        weekdays: "ap-calendar-weekdays",
        weekday: "ap-calendar-weekday",
        weeks: "ap-calendar-weeks",
        week: "ap-calendar-week",
        day: "ap-calendar-day",
        day_button: "ap-calendar-day-button",
        selected: "ap-calendar-selected",
        today: "ap-calendar-today",
        outside: "ap-calendar-outside",
        disabled: "ap-calendar-disabled",
        range_start: "ap-calendar-range-start",
        range_middle: "ap-calendar-range-middle",
        range_end: "ap-calendar-range-end",
        hidden: "ap-calendar-hidden",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: iconClassName }) =>
          orientation === "left" ? (
            <ChevronLeft className={cn("h-4 w-4", iconClassName)} />
          ) : (
            <ChevronRight className={cn("h-4 w-4", iconClassName)} />
          ),
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
